import { Box } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Home } from "./home/Home";
import { NotificationsPage } from "./notifications/NotificationsPage";
import { ContractStatusDto } from "../autogen/bff-api";
import { AssistantPage } from "../common/agent/AssistantPage";
import { Admin } from "./admin";
import { urls } from "../urls";
import { EditContract } from "./contracts/edit/EditContract";
import { ContractSearchPage } from "./contracts/search/ContractSearchPage";
import { Contracts } from "./contracts/view-all/Contracts";
import { ContractTasks } from "./contracts/view-all-tasks/ContractTasks";
import { ContractPage } from "./contracts/view-single/ContractPage";
import { AccessManagementPage } from "./organizations/current/manage-users/AccessManagementPage";
import { EditOrganizationProfile } from "./organizations/current/edit/EditOrganizationProfile";
import { OrganizationsPage } from "./organizations/search/OrganizationsPage";
import { ViewOrganizationProfile } from "./organizations/view-single/ViewOrganizationProfile";
import { Projects } from "./projects/view-all/Projects";
import { ProjectPage } from "./projects/view-single/ProjectPage";
import { EditSourcingEventWizard } from "./sourcing-events/buyer/wizard/EditSourcingEventWizard";
import { LegacyWizardRedirect } from "./sourcing-events/buyer/wizard/LegacyWizardRedirect";
import { EditBidPage } from "./sourcing-events/portal/bid/EditBidPage";
import { EditPrivateBidPage } from "./sourcing-events/portal/bid/EditPrivateBidPage";
import { EditParticipationRequestPage } from "./sourcing-events/portal/request/EditParticipationRequestPage";
import { PortalSourcingEventsPage } from "./sourcing-events/portal/view-all/PortalSourcingEventsPage";
import { PortalSourcingEventPage } from "./sourcing-events/portal/view-single/PortalSourcingEventPage";
import { SupplierBidsList } from "./supplier/SupplierBidsList";
import { SupplierInvitationsList } from "./supplier/SupplierInvitationsList";
import { ContributingEntityPage } from "./sourcing-events/portal/contributing/ContributingEntityPage";
import { ContributingEntityResponsePage } from "./sourcing-events/portal/contributing/ContributingEntityResponsePage";
import { SupplierContributionsList } from "./sourcing-events/portal/contributing/SupplierContributionsList";
import { Behovsliste } from "./behov/Behovsliste";
import { NeedsRoute } from "./behov/NeedsRoute";
import { EditNeed } from "./behov/EditNeed";
import { MineBehov } from "./behov/MineBehov";
import { NeedView } from "./behov/NeedView";
import { NewNeed } from "./behov/NewNeed";
import { EditProfilePage } from "./profile/EditProfilePage";
import { ProfilePage } from "./profile/ProfilePage";
import { SettingsPage } from "./settings/SettingsPage";
import { Kravbibliotek } from "./kravbibliotek/Kravbibliotek";
import { MalEditor } from "./maler/MalEditor";
import { SourcingEventTemplates } from "./maler/SourcingEventTemplates";
import { SourcingEventsList } from "./SourcingEventsList";
import { BidDetailView } from "./sourcing-event/bid-detail/BidDetailView";
import { EditNegotiationRoundWizard } from "./sourcing-event/negotiation/wizard/EditNegotiationRoundWizard";
import { RequestDetailView } from "./sourcing-event/qualification/detail/RequestDetailView";
import { ProtocolPage } from "./sourcing-event/protocol/ProtocolPage";
import { SourcingEventView } from "./sourcing-event/SourcingEventView";

type AppShellRoute = { path: string; element: ReactElement; fullWidth?: boolean; fillHeight?: boolean };

const contributingEntityRoutes: AppShellRoute[] = [
  { path: urls.app.supplier.contributions, element: <SupplierContributionsList />, fullWidth: true },
  { path: urls.app.supplier.contribution.pathname, element: <ContributingEntityPage /> },
  {
    path: urls.app.supplier.contributionReview.pathname,
    element: <ContributingEntityResponsePage />,
  },
];

export const appShellRoutes: AppShellRoute[] = [
  { path: urls.admin, element: <Admin />, fullWidth: true },
  { path: urls.app.home, element: <Home /> },
  { path: urls.app.notifications, element: <NotificationsPage /> },
  { path: urls.app.assistant, element: <AssistantPage /> },
  { path: urls.app.needs.new, element: <NeedsRoute><NewNeed /></NeedsRoute> },
  { path: urls.app.needs.edit.pathname, element: <NeedsRoute><EditNeed /></NeedsRoute> },
  { path: urls.app.needs.view.pathname, element: <NeedsRoute><NeedView /></NeedsRoute> },
  { path: urls.app.needs.mine, element: <NeedsRoute><MineBehov /></NeedsRoute>, fullWidth: true },
  { path: urls.app.needs.list, element: <NeedsRoute><Behovsliste /></NeedsRoute>, fullWidth: true },
  { path: urls.app.sourcingEvents.viewAll, element: <SourcingEventsList />, fullWidth: true },
  { path: urls.app.kravbibliotek, element: <Kravbibliotek />, fullWidth: true },
  { path: urls.app.sourcingEvents.view.pathname, element: <SourcingEventView /> },
  { path: urls.app.sourcingEvents.bidDetail.pathname, element: <BidDetailView /> },
  { path: urls.app.sourcingEvents.participationRequestDetail.pathname, element: <RequestDetailView /> },
  { path: urls.app.sourcingEvents.protocol.pathname, element: <ProtocolPage /> },
  { path: urls.app.supplier.procurements, element: <PortalSourcingEventsPage variant="browse" />, fullWidth: true },
  { path: urls.app.supplier.procurement.pathname, element: <PortalSourcingEventPage /> },
  { path: urls.app.supplier.bids, element: <SupplierBidsList />, fullWidth: true },
  { path: urls.app.supplier.bid.edit.paths.intro.pathname, element: <EditBidPage /> },
  { path: urls.app.supplier.privateBid.edit.pathname, element: <EditPrivateBidPage /> },
  { path: urls.app.supplier.invitations, element: <SupplierInvitationsList />, fullWidth: true },
  { path: "/mine-foresporsler", element: <Navigate to={urls.app.supplier.invitations} replace /> },
  { path: urls.app.supplier.request.edit.paths.intro.pathname, element: <EditParticipationRequestPage /> },
  {
    path: urls.app.sourcingEvents.edit.paths.info.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditSourcingEventWizard />
      </Box>
    ),
  },
  {
    path: urls.app.sourcingEvents.edit.legacyPathname,
    element: <LegacyWizardRedirect />,
  },
  {
    path: urls.app.sourcingEvents.editNegotiationRound.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditNegotiationRoundWizard />
      </Box>
    ),
  },
  {
    path: urls.app.contracts.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-all" />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.active,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-active" defaultStatuses={[ContractStatusDto.Active, ContractStatusDto.Awarded]} />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.search,
    element: <ContractSearchPage />,
    fillHeight: true,
  },
  {
    path: urls.app.contracts.tasks,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractTasks />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractPage />
      </Box>
    ),
  },
  {
    path: urls.app.contracts.edit.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditContract />
      </Box>
    ),
  },
  {
    path: urls.app.projects.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Projects />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.projects.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ProjectPage />
      </Box>
    ),
  },
  { path: urls.app.profile, element: <ProfilePage /> },
  { path: urls.app.profileEdit, element: <EditProfilePage /> },
  { path: urls.app.settings, element: <SettingsPage /> },
  { path: urls.app.maler.list, element: <SourcingEventTemplates />, fullWidth: true },
  { path: urls.app.maler.new.pathname, element: <MalEditor /> },
  { path: urls.app.maler.edit.pathname, element: <MalEditor /> },
  {
    path: urls.app.users,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px="6" pb="6">
        <AccessManagementPage />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.organizations,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="2">
        <OrganizationsPage />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: `${urls.app.organizationProfile.pathname}/*`,
    element: <ViewOrganizationProfile />,
  },
  {
    path: urls.app.organizationProfileEdit,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} pb="6">
        <EditOrganizationProfile />
      </Box>
    ),
  },
  ...contributingEntityRoutes,
];

